import { clsx } from '@alexis/helpers/clsx';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LogSlider from '@components/LogSlider';
import Price from '@components/Price';

import { getRange } from '@helpers/flight';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { changeShowTotalPrice } from '@redux/actions/priceOptionsActions';
import {
  getCurrencyState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilters.module.scss';

type MultiCityPriceFilterProps = {
  genzoTrackApplyFilterSearchView: () => void;
  genzoTrackApplyFilterActionEvent: any;
};

const MultiCityPriceFilter: React.FC<MultiCityPriceFilterProps> = ({
  genzoTrackApplyFilterSearchView,
  genzoTrackApplyFilterActionEvent,
}) => {
  const { pricesFilter, changeMultiCityRangeFilter } = useFlightSearchFilters();

  const { exchangeRate, minUsdPrice, maxUsdPrice } = useFlightSearchResults();

  const selectedPriceRange = useMemo(
    () => pricesFilter.find((filter) => filter.leg === 0)?.values || [],
    [pricesFilter],
  );

  const [isPriceFilterExpanded, setIsPriceFilterExpanded] = useState(true);
  const [isPriceTypeDropdownExpanded, setIsPriceTypeDropdownExpanded] = useState(false);
  const [newSelectedPrices, setNewSelectedPrices] = useState<number[]>(selectedPriceRange);

  const locale = useSelector(getLocaleState);
  const currency = useSelector(getCurrencyState);
  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);
  const { showTotalPrice } = useSelector(getPriceOptionsState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const dispatch = useDispatch();

  const priceSliderMinimumDistance = (() => {
    if (!!minUsdPrice && !!maxUsdPrice) {
      const priceRange = maxUsdPrice - minUsdPrice;

      if (priceRange > 1000) {
        return 20;
      }

      if (priceRange < 1001) {
        return 10;
      }

      if (priceRange < 101) {
        return 1;
      }
    }

    return 0;
  })();

  const selectedPrices = useMemo(
    () => (selectedPriceRange.length ? selectedPriceRange : [minUsdPrice || 0, maxUsdPrice || 0]),
    [maxUsdPrice, minUsdPrice, selectedPriceRange],
  );

  useEffect(() => {
    setNewSelectedPrices(selectedPrices);
  }, [selectedPrices]);

  useEffect(() => {
    if (isPriceTypeDropdownExpanded) {
      const handleBodyOnClick = (): void => {
        setIsPriceTypeDropdownExpanded(false);
      };

      const timerId = setTimeout(() => {
        document.body.addEventListener('click', handleBodyOnClick);
      }, 0);

      return () => {
        clearTimeout(timerId);
        document.body.removeEventListener('click', handleBodyOnClick);
      };
    }
  }, [isPriceTypeDropdownExpanded]);

  const handlePriceTypeChange = (showTotalPrice: boolean) => {
    dispatch(changeShowTotalPrice(showTotalPrice));

    setIsPriceTypeDropdownExpanded(false);
  };

  const handleSelectedPricesChanged = (prices: number[]) => {
    const newFilter = changeMultiCityRangeFilter({
      activeLeg: 0,
      searchParamKey: FlightSearchResultSearchParams.Price,
      max: maxUsdPrice,
      min: minUsdPrice,
      range: prices,
    });

    const selected_amount_min_usd = prices[0];
    const selected_amount_max_usd = prices[1];
    const isPriceFilterApplied = !!newFilter.length;
    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      price_min_max_usd: isPriceFilterApplied ? [minUsdPrice, maxUsdPrice] : undefined,
      price_selected_usd: isPriceFilterApplied
        ? [selected_amount_min_usd, selected_amount_max_usd]
        : undefined,
    });
    genzoTrackApplyFilterSearchView();
  };

  if (!minUsdPrice || !maxUsdPrice) {
    return null;
  }

  return (
    <>
      <div
        className={clsx(
          styles.categoryHeader,
          styles.withSubHeader,
          isPriceFilterExpanded && styles.expanded,
        )}
        onClick={() => setIsPriceFilterExpanded((prev) => !prev)}
      >
        <div>
          {translations.total_trip_price}
          {!!pricesFilter.length && <div className={styles.isFilteredBy} />}
        </div>

        <i className={clsx(styles.chevron, styles.bottom)}></i>
      </div>

      <div className={styles.subHeader}>{translations.whole_trip}</div>

      {isPriceFilterExpanded ? (
        <div className={styles.categoryContent}>
          <div className={styles.sliderContainer} data-testid='price-slider-container'>
            <div className={styles.labelRange}>
              <Price
                isInline
                className={styles.label}
                price={{
                  amount: newSelectedPrices[0] ?? minUsdPrice,
                  amountUsd: newSelectedPrices[0] ?? minUsdPrice,
                  currencyCode: 'USD',
                }}
                locale={locale}
                currency={currency}
                exchangeRate={exchangeRate}
              />
              <Price
                isInline
                className={styles.label}
                price={{
                  amount: newSelectedPrices[1] ?? maxUsdPrice,
                  amountUsd: newSelectedPrices[1] ?? maxUsdPrice,
                  currencyCode: 'USD',
                }}
                locale={locale}
                currency={currency}
                exchangeRate={exchangeRate}
              />
            </div>

            <LogSlider
              dataPw='flightFilters_price'
              isRtl={isRtl}
              max={maxUsdPrice}
              min={minUsdPrice}
              onChange={(values, activeThumb) => {
                const priceRange = getRange({
                  activeThumb,
                  values,
                  max: maxUsdPrice,
                  min: minUsdPrice,
                  minimumDistance: priceSliderMinimumDistance,
                });

                if (priceRange) setNewSelectedPrices(priceRange);
              }}
              onChangeCommitted={(values, activeThumb) => {
                recordChangeFilterAction();

                const priceRange = getRange({
                  activeThumb,
                  values,
                  max: maxUsdPrice,
                  min: minUsdPrice,
                  minimumDistance: priceSliderMinimumDistance,
                });

                if (priceRange) handleSelectedPricesChanged(priceRange);
              }}
              value={newSelectedPrices}
            />
          </div>

          <button
            className={clsx(styles.priceType, isPriceTypeDropdownExpanded && styles.expanded)}
            onClick={() => setIsPriceTypeDropdownExpanded((prev) => !prev)}
          >
            <div className={styles.labelsContainer}>
              <div className={styles.priceTypeLabel}>
                {showTotalPrice ? translations.total_price : translations.per_person}
              </div>
              <div className={styles.isIncludingTaxLabel}>{translations.include_tax}</div>
            </div>
            <i className={clsx(styles.triangle, styles.down)}></i>

            {/* Price type dropdown */}
            {isPriceTypeDropdownExpanded ? (
              <div className={styles.dropdown} onClick={(e) => e.stopPropagation()}>
                <div
                  className={clsx(styles.item, !showTotalPrice && styles.active)}
                  onClick={() => handlePriceTypeChange(false)}
                >
                  {translations.per_person}
                </div>

                <div
                  className={clsx(styles.item, showTotalPrice && styles.active)}
                  onClick={() => handlePriceTypeChange(true)}
                >
                  {translations.total_price}
                </div>
              </div>
            ) : null}
          </button>
        </div>
      ) : null}
    </>
  );
};

export default MultiCityPriceFilter;
